import React, { useState, useEffect } from "react";
import { getAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement } from "../../../APIClient/apiClient";
import Toast from "../../../component/Toast";
import "../../../style/admin/AnnouncementManager.css";

const AnnouncementManager = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    en: { message: "" },
    vi: { message: "" },
    is_active: true,
    sort_order: 0,
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const data = await getAnnouncements();
      setAnnouncements(data);
    } catch (err) {
      setToast({ message: "Failed to load announcements", type: "error" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (language, field, value) => {
    setFormData({
      ...formData,
      [language]: {
        ...formData[language],
        [field]: value,
      },
    });
  };

  const handleMetaChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateAnnouncement(editingId, formData);
      } else {
        await createAnnouncement(formData);
      }
      fetchAnnouncements();
      resetForm();
      setToast({ message: "Announcement saved successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to save announcement", type: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this announcement?")) {
      try {
        await deleteAnnouncement(id);
        fetchAnnouncements();
        setToast({ message: "Announcement deleted successfully", type: "success" });
      } catch (err) {
        setToast({ message: "Failed to delete announcement", type: "error" });
      }
    }
  };

  const handleEdit = (announcement) => {
    setFormData({
      en: { message: announcement.translations.find((t) => t.language === "en")?.message || "" },
      vi: { message: announcement.translations.find((t) => t.language === "vi")?.message || "" },
      is_active: announcement.is_active,
      sort_order: announcement.sort_order,
    });
    setEditingId(announcement.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      en: { message: "" },
      vi: { message: "" },
      is_active: true,
      sort_order: 0,
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return <div className="admin-manager-loading">Loading announcements...</div>;

  return (
    <div className="admin-manager">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="admin-manager-header">
        <button className="admin-manager-btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? "Cancel" : "Add New Announcement"}
        </button>
      </div>

      {showForm && (
        <div className="admin-manager-form">
          <h3>{editingId ? "Edit Announcement" : "Create New Announcement"}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h4>English</h4>
              <textarea
                value={formData.en.message}
                onChange={(e) => handleInputChange("en", "message", e.target.value)}
                placeholder="English message"
                required
              />
            </div>

            <div className="form-section">
              <h4>Vietnamese</h4>
              <textarea
                value={formData.vi.message}
                onChange={(e) => handleInputChange("vi", "message", e.target.value)}
                placeholder="Vietnamese message"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Sort Order</label>
                <input type="number" value={formData.sort_order} onChange={(e) => handleMetaChange("sort_order", parseInt(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Active</label>
                <input type="checkbox" checked={formData.is_active} onChange={(e) => handleMetaChange("is_active", e.target.checked)} />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? "Update" : "Create"} Announcement
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-manager-table">
        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>English Message</th>
              <th>Vietnamese Message</th>
              <th>Active</th>
              <th>Sort Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {announcements.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
                  No announcements found
                </td>
              </tr>
            ) : (
              announcements.map((announcement, index) => (
                <tr key={announcement.id}>
                  <td>{index + 1}</td>
                  <td>{announcement.translations.find((t) => t.language === "en")?.message.substring(0, 50)}...</td>
                  <td>{announcement.translations.find((t) => t.language === "vi")?.message.substring(0, 50)}...</td>
                  <td>{announcement.is_active ? "Yes" : "No"}</td>
                  <td>{announcement.sort_order}</td>
                  <td className="action-buttons">
                    <button className="btn-edit" onClick={() => handleEdit(announcement)}>
                      Edit
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(announcement.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AnnouncementManager;
